import { CONDUCT_ALERT_LIMITS } from '@lume/shared';
import { useState } from 'react';

import type { ConductAlertConfigWire } from '../../../../lib/api-client';
import type { ConductAlertConfig } from '@lume/shared';
import type React from 'react';

interface Props {
  config: ConductAlertConfigWire;
  saving: boolean;
  conflict: boolean;
  otherError: boolean;
  onSave: (body: Partial<ConductAlertConfig>) => void;
}

/**
 * Editor inline do alerta de conduta (S4). O alerta é um SINAL para a conversa
 * de gestão — jamais bloqueia envio nem pune. O componente é remontado
 * (`key={config.version}`) a cada save, então o estado local sempre parte da
 * config vigente.
 */
export function ConductAlertEditor({
  config,
  saving,
  conflict,
  otherError,
  onSave,
}: Props): React.JSX.Element {
  const [enabled, setEnabled] = useState(config.enabled);
  const [thresholdPct, setThresholdPct] = useState(config.pastedWithoutEditThresholdPct);
  const [windowDays, setWindowDays] = useState(config.windowDays);
  const [minSampleSize, setMinSampleSize] = useState(config.minSampleSize);

  const dirty =
    enabled !== config.enabled ||
    thresholdPct !== config.pastedWithoutEditThresholdPct ||
    windowDays !== config.windowDays ||
    minSampleSize !== config.minSampleSize;

  return (
    <div className="rp-alert-editor">
      <p className="tweaks-hint" style={{ marginBottom: 'var(--space-3)' }}>
        Sinal para conversa de gestão — não bloqueia envio nem pune.
      </p>

      <label className="rp-alert-editor__toggle">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => {
            setEnabled(e.target.checked);
          }}
        />
        <span>Destacar operadores acima do limiar</span>
      </label>

      <div className="rp-alert-editor__fields">
        <label>
          <span>Limiar de coladas sem edição (%)</span>
          <input
            type="number"
            min={CONDUCT_ALERT_LIMITS.minThresholdPct}
            max={CONDUCT_ALERT_LIMITS.maxThresholdPct}
            value={thresholdPct}
            onChange={(e) => {
              setThresholdPct(Number(e.target.value));
            }}
          />
        </label>
        <label>
          <span>Janela (dias)</span>
          <input
            type="number"
            min={CONDUCT_ALERT_LIMITS.minWindowDays}
            max={CONDUCT_ALERT_LIMITS.maxWindowDays}
            value={windowDays}
            onChange={(e) => {
              setWindowDays(Number(e.target.value));
            }}
          />
        </label>
        <label>
          <span>Amostra mínima (envios)</span>
          <input
            type="number"
            min={CONDUCT_ALERT_LIMITS.minMinSampleSize}
            max={CONDUCT_ALERT_LIMITS.maxMinSampleSize}
            value={minSampleSize}
            onChange={(e) => {
              setMinSampleSize(Number(e.target.value));
            }}
          />
        </label>
      </div>

      {conflict && (
        <div className="banner banner--danger" role="alert" style={{ marginTop: 'var(--space-3)' }}>
          <div className="banner__body">
            <span className="banner__title">
              A configuração mudou em outra sessão. Recarregue a página e tente de novo.
            </span>
          </div>
        </div>
      )}
      {otherError && (
        <div className="banner banner--danger" role="alert" style={{ marginTop: 'var(--space-3)' }}>
          <div className="banner__body">
            <span className="banner__title">Falha ao salvar a configuração.</span>
          </div>
        </div>
      )}

      <div style={{ marginTop: 'var(--space-3)' }}>
        <button
          type="button"
          className="chip chip--active"
          disabled={saving || !dirty}
          onClick={() => {
            onSave({
              enabled,
              pastedWithoutEditThresholdPct: thresholdPct,
              windowDays,
              minSampleSize,
            });
          }}
        >
          {saving ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}
